import { TEST_HUB_URL } from '@/constants/constants';
import { apiFetch } from '@/utils/apiWrapper';
import { Extension } from '../Extensions/extensions.service';
import { Feature } from '../Features/features.service';
import { PricingConfigurationValues } from '../Pricing/pricing-configuration.service';

// Base URL for plans endpoints
const plansUrl = `${TEST_HUB_URL}subscriptions/plan/`;
const subscriptionsOperationsUrl = `${TEST_HUB_URL}subscriptions/subscription-operations/`;

// Types for Plan data

export enum FeatureGroupNameKeys {
  ACCOUNT_RESTRICTIONS = 'account_restrictions',
  MENU_RESTRICTIONS = 'menu_restrictions',
}
export interface FeatureGroup {
  _id: string;
  name: string;
  features: Feature[];
}

export interface PlanBuild {
  _id: string;
  planId: string;
  buildId: string;
  createdAt: string;
  updatedAt: string;
}

export interface Plan {
  _id: string;
  name: string;
  description: string;
  companyId: string;
  price: number | null;
  extensions: Extension[];
  featureGroups: {
    [key: string]: FeatureGroup;
  };
  isActive?: boolean;
  checkoutFlows: PlanBuild[];
  billingInterval?: 'month' | 'year';
  createdAt: string;
  updatedAt: string;
}

// Update plan DTO
export interface UpdatePlanDTO {
  name?: string;
  description?: string;
  price?: number;
  features?: string[];
  isActive?: boolean;
  organizationId?: string;
  billingInterval?: 'month' | 'year';
  checkoutFlows?: string[];
  extensions?: string[];
}

// Plan filters
export interface PlanFilters {
  organizationId?: string;
  isActive?: boolean;
}

export type CreatePlanFeature = {
  guid: string;
  value: string;
  limit?: string | number;
};

export type CreatePlanDTO = {
  name: string;
  features: CreatePlanFeature[];
  organizationId: string;
  extensions?: string[];
  builds?: string[];
};

/**
 * Fetches all plans with optional filters
 * @param filters Optional filters for the plans
 * @returns Array of plans
 */
export async function getPlans(filters: PlanFilters): Promise<Plan[]> {
  try {
    let url = `${subscriptionsOperationsUrl}plan/get-all/${filters.organizationId}`;

    // Add query parameters if filters provided
    if (filters) {
      const queryParams = new URLSearchParams();

      if (filters.isActive !== undefined) {
        queryParams.append('isActive', String(filters.isActive));
      }

      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    const response = await apiFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response) {
      throw new Error('Error fetching plans');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching plans:', error);
    throw error;
  }
}

/**
 * Fetches a plan by ID
 * @param id Plan ID
 * @param organizationId Organization ID
 * @returns Plan data
 */
export async function getPlanById(id: string, organizationId: string): Promise<Plan> {
  try {
    const url = `${subscriptionsOperationsUrl}plan/${organizationId}/${id}`;

    const response = await apiFetch(url);

    if (!response) {
      throw new Error(`Could not fetch plan with ID: ${id}`);
    }

    const data = await response.json();
    if (data || data.length > 0) {
      return data[0];
    } else {
      throw new Error(`Could not fetch plan with ID: ${id}`);
    }
  } catch (error) {
    console.error(`Error fetching plan with ID ${id}:`, error);
    throw error;
  }
}

/**
 * Creates a new plan
 * @param planData Plan data to create
 * @param organizationId Organization ID
 * @returns The created plan
 */
export async function createPlan(planData: CreatePlanDTO, organizationId: string): Promise<Plan> {
  try {
    const url = `${subscriptionsOperationsUrl}create-company-plan/${organizationId}`;
    const response = await apiFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(planData),
    });

    if (!response) {
      throw new Error('Error creating plan');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating plan:', error);
    throw error;
  }
}

/**
 * Updates a plan
 * @param id Plan ID to update
 * @param planData Updated plan data
 * @param organizationId Organization ID
 * @returns The updated plan
 */
export async function updatePlan(
  id: string,
  planData: CreatePlanDTO,
  organizationId: string,
): Promise<Plan> {
  try {
    const url = `${subscriptionsOperationsUrl}update-company-plan/${organizationId}/${id}`;

    const response = await apiFetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(planData),
    });

    if (!response) {
      throw new Error(`Could not update plan with ID: ${id}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error updating plan with ID ${id}:`, error);
    throw error;
  }
}

/**
 * Deletes a plan
 * @param id Plan ID to delete
 * @param organizationId Organization ID
 * @returns Success message
 */
export async function deletePlan(
  id: string,
  organizationId: string,
): Promise<{ success: boolean; message: string }> {
  try {
    const url = `${plansUrl}${organizationId}/${id}`;

    const response = await apiFetch(url, {
      method: 'DELETE',
    });

    if (!response) {
      throw new Error(`Could not delete plan with ID: ${id}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error deleting plan with ID ${id}:`, error);
    throw error;
  }
}

/**
 * Fetches plans for a specific organization
 * @param organizationId Organization ID
 * @returns Array of plans
 */
export async function getOrganizationPlans(organizationId: string): Promise<Plan[]> {
  return getPlans({ organizationId });
}

export async function getPlanByCompanyId(companyId: string, organizationId: string): Promise<Plan> {
  try {
    const url = `${subscriptionsOperationsUrl}plan/plan-by-company/${organizationId}/${companyId}`;
    const response = await apiFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!response) {
      throw new Error(`Could not fetch plan with ID: ${companyId}`);
    }
    const data = await response.json();
    return data[0];
  } catch (error) {
    console.error(`Error fetching plan with ID ${companyId}:`, error);
    throw error;
  }
}

export type CreateOrUpdateCustomPlanDTO = {
  planData?: CreatePlanDTO | null;
  pricingData?: PricingConfigurationValues | null;
  organizationId: string;
  companyId: string;
};

export async function createOrUpdateCustomPlan({
  planData,
  pricingData,
  organizationId,
  companyId,
}: CreateOrUpdateCustomPlanDTO) {
  try {
    const body = {
      plan: planData,
      pricing: pricingData ? { values: pricingData } : undefined,
    };
    const url = `${subscriptionsOperationsUrl}custom-plan/${organizationId}/${companyId}`;
    const response = await apiFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (!response) {
      throw new Error('Error creating or updating custom plan');
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error creating or updating custom plan:`, error);
    throw error;
  }
}
